import { useCallback, useEffect } from "react";
import { useAICheckStore } from "@/stores/aicheck";
import type {
  FocusedEvidenceTarget,
  TraceabilityHistoryState,
  TraceabilityTab,
} from "@/types/aicheck";

export type ActiveTab =
  | TraceabilityTab
  | "comparison"
  | "freeform"
  | "history"
  | "findings";

export const TRACEABILITY_HISTORY_KEY = "__aicheckTraceability";

export function isTraceabilityTab(value: unknown): value is TraceabilityTab {
  return (
    value === "basic" ||
    value === "review" ||
    value === "reminders" ||
    value === "expert-knowledge"
  );
}

export function isActiveTab(value: unknown): value is ActiveTab {
  return (
    isTraceabilityTab(value) ||
    value === "comparison" ||
    value === "freeform" ||
    value === "history" ||
    value === "findings"
  );
}

export function normalizeTraceabilityHistoryState(
  value: unknown,
): TraceabilityHistoryState | null {
  if (typeof value !== "object" || value === null) return null;

  const candidate = value as Record<string, unknown>;
  if (
    (candidate.kind !== "tab" &&
      candidate.kind !== "summary-finding" &&
      candidate.kind !== "check" &&
      candidate.kind !== "evidence") ||
    !isTraceabilityTab(candidate.tab)
  ) {
    return null;
  }

  return {
    kind: candidate.kind,
    tab: candidate.tab,
    summaryFindingIndex:
      typeof candidate.summaryFindingIndex === "number"
        ? candidate.summaryFindingIndex
        : undefined,
    checkId:
      typeof candidate.checkId === "string" ? candidate.checkId : undefined,
    evidenceIndex:
      typeof candidate.evidenceIndex === "number"
        ? candidate.evidenceIndex
        : undefined,
    returnFindingIndex:
      typeof candidate.returnFindingIndex === "number"
        ? candidate.returnFindingIndex
        : undefined,
  };
}

export interface UseTraceabilityHistoryOptions {
  /** Component-owned active-tab setter, driven by history navigation. */
  setActiveTab: (tab: ActiveTab) => void;
}

export interface UseTraceabilityHistoryResult {
  pushTraceabilityHistory: (
    state: TraceabilityHistoryState,
    mode?: "push" | "replace",
  ) => void;
}

/**
 * Deep-link / traceability history: manages window.history push/replace,
 * applies persisted history state to the focus store, and wires the popstate
 * listener. Behavior is identical to the previous in-component implementation.
 */
export function useTraceabilityHistory({
  setActiveTab,
}: UseTraceabilityHistoryOptions): UseTraceabilityHistoryResult {
  const setFocusedSummaryFindingIndex = useAICheckStore(
    (state) => state.setFocusedSummaryFindingIndex,
  );
  const setFocusedCheckId = useAICheckStore((state) => state.setFocusedCheckId);
  const setFocusedEvidenceTarget = useAICheckStore(
    (state) => state.setFocusedEvidenceTarget,
  );
  const setHighlightCheckId = useAICheckStore(
    (state) => state.setHighlightCheckId,
  );
  const setHighlightEvidenceTarget = useAICheckStore(
    (state) => state.setHighlightEvidenceTarget,
  );
  const setSummaryReturnTarget = useAICheckStore(
    (state) => state.setSummaryReturnTarget,
  );

  const pushTraceabilityHistory = useCallback(
    (state: TraceabilityHistoryState, mode: "push" | "replace" = "push") => {
      if (typeof window === "undefined") return;
      const historyState = {
        ...(window.history.state ?? {}),
        [TRACEABILITY_HISTORY_KEY]: state,
      };
      window.history[mode === "replace" ? "replaceState" : "pushState"](
        historyState,
        "",
        window.location.href,
      );
    },
    [],
  );

  const applyTraceabilityHistoryState = useCallback(
    (state: TraceabilityHistoryState | null) => {
      setFocusedSummaryFindingIndex(null);
      setFocusedCheckId(null);
      setFocusedEvidenceTarget(null);
      setHighlightCheckId(null);
      setHighlightEvidenceTarget(null);
      setSummaryReturnTarget(null);

      const nextTab = state?.tab ?? "basic";
      setActiveTab(nextTab);

      if (!state) {
        return;
      }

      if (
        state.returnFindingIndex != null &&
        typeof state.checkId === "string" &&
        isActiveTab(state.tab)
      ) {
        setSummaryReturnTarget({
          tab: state.tab,
          checkId: state.checkId,
          findingIndex: state.returnFindingIndex,
        });
      }

      if (state.kind === "summary-finding") {
        if (typeof state.summaryFindingIndex === "number") {
          setFocusedSummaryFindingIndex(state.summaryFindingIndex);
        }
        return;
      }

      if (state.kind === "check" && typeof state.checkId === "string") {
        setFocusedCheckId(state.checkId);
        return;
      }

      if (
        state.kind === "evidence" &&
        typeof state.checkId === "string" &&
        typeof state.evidenceIndex === "number"
      ) {
        setFocusedEvidenceTarget({
          checkId: state.checkId,
          evidenceIndex: state.evidenceIndex,
        } satisfies FocusedEvidenceTarget);
      }
    },
    [
      setActiveTab,
      setFocusedCheckId,
      setFocusedEvidenceTarget,
      setFocusedSummaryFindingIndex,
      setHighlightCheckId,
      setHighlightEvidenceTarget,
      setSummaryReturnTarget,
    ],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const initialState = normalizeTraceabilityHistoryState(
      window.history.state?.[TRACEABILITY_HISTORY_KEY],
    );

    if (initialState) {
      applyTraceabilityHistoryState(initialState);
    } else {
      pushTraceabilityHistory({ kind: "tab", tab: "basic" }, "replace");
    }

    const handlePopState = (event: PopStateEvent) => {
      applyTraceabilityHistoryState(
        normalizeTraceabilityHistoryState(
          (event.state as Record<string, unknown> | null)?.[
            TRACEABILITY_HISTORY_KEY
          ],
        ),
      );
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [applyTraceabilityHistoryState, pushTraceabilityHistory]);

  return { pushTraceabilityHistory };
}
